// Searchable list of every clip, used by Explore
import React, { useMemo, useState } from 'react';
import { Check, Heart, Search, Star, X } from 'lucide-react';
import { CATEGORIES, TAGS } from '@/data';
import { ClipThumb } from '@/components/ClipThumb';
import { getClipCategoryIds } from '@/types';
import type { Clip } from '@/types';

type Sort = 'match' | 'popular' | 'az';
type Show = 'all' | 'untried' | 'wishlist' | 'tried';

const categoryName = (id: string) => CATEGORIES.find(c => c.id === id)?.name ?? '';
const tagLabel = (id: string) => TAGS.find(t => t.id === id)?.label ?? id;

// Every word in the query has to appear somewhere; title hits rank highest
const scoreClip = (clip: Clip, words: string[]) => {
  const title = clip.title.toLowerCase();
  const cats = getClipCategoryIds(clip).map(categoryName).join(' ').toLowerCase();
  const tags = (clip.tags || []).map(tagLabel).join(' ').toLowerCase();
  const rest = [clip.description, clip.notesForHim, clip.notesForHer].join(' ').toLowerCase();
  let score = 0;
  for (const w of words) {
    if (title.startsWith(w)) score += 6;
    else if (title.includes(w)) score += 4;
    else if (cats.includes(w) || tags.includes(w)) score += 3;
    else if (rest.includes(w)) score += 1;
    else return 0;
  }
  return score;
};

interface ClipBrowserProps {
  clips: Clip[];
  query: string;
  triedIds: Set<string>;
  wishlistIds: Set<string>;
  onOpen: (clip: Clip) => void;
}

export const ClipSearchField: React.FC<{ query: string; onQueryChange: (q: string) => void; autoFocus?: boolean; onFocus?: () => void }> = ({ query, onQueryChange, autoFocus, onFocus }) => (
  <label className="relative block">
    <span className="sr-only">Search clips</span>
    <Search className="w-[18px] h-[18px] text-ink-muted absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
    <input
      type="search"
      value={query}
      autoFocus={autoFocus}
      onFocus={onFocus}
      onChange={e => onQueryChange(e.target.value)}
      placeholder="Search titles, positions, tags…"
      className="w-full h-12 rounded-2xl bg-surface border border-line pl-11 pr-11 text-[15px] text-ink placeholder:text-ink-muted focus:outline-none focus:border-action [&::-webkit-search-cancel-button]:hidden"
    />
    {query && (
      <button onClick={() => onQueryChange('')} aria-label="Clear search" className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center text-ink-muted hover:text-ink">
        <X className="w-4 h-4" />
      </button>
    )}
  </label>
);

export const ClipBrowser: React.FC<ClipBrowserProps> = ({ clips, query, triedIds, wishlistIds, onOpen }) => {
  const [sort, setSort] = useState<Sort>('match');
  const [show, setShow] = useState<Show>('all');
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const searching = words.length > 0;

  const results = useMemo(() => {
    const scored = clips
      .map(clip => ({ clip, score: searching ? scoreClip(clip, words) : 1 }))
      .filter(r => r.score > 0)
      .filter(({ clip }) =>
        show === 'all' ? true
        : show === 'untried' ? !triedIds.has(clip.id)
        : show === 'tried' ? triedIds.has(clip.id)
        : wishlistIds.has(clip.id));
    const effectiveSort = sort === 'match' && !searching ? 'popular' : sort;
    return scored.sort((a, b) =>
      effectiveSort === 'match' ? b.score - a.score || a.clip.title.localeCompare(b.clip.title)
      : effectiveSort === 'popular' ? (b.clip.couplesTried || 0) - (a.clip.couplesTried || 0) || a.clip.title.localeCompare(b.clip.title)
      : a.clip.title.localeCompare(b.clip.title)).map(r => r.clip);
  }, [clips, query, sort, show, triedIds, wishlistIds]);

  const showChips: { id: Show; label: string }[] = [
    { id: 'all', label: 'All' },
    { id: 'untried', label: 'Not tried yet' },
    { id: 'wishlist', label: 'Want to try' },
    { id: 'tried', label: 'Tried' },
  ];
  const chip = (active: boolean) =>
    `shrink-0 h-9 px-3.5 rounded-full border text-[13px] transition-colors ${active ? 'bg-ink text-ground border-ink font-semibold' : 'bg-raised border-line text-ink-2 font-medium hover:text-ink'}`;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 overflow-x-auto no-scrollbar -mx-5 px-5">
        {showChips.map(c => (
          <button key={c.id} onClick={() => setShow(c.id)} aria-pressed={show === c.id} className={chip(show === c.id)}>{c.label}</button>
        ))}
      </div>
      <div className="flex items-center justify-between gap-3">
        <p className="text-[13px] text-ink-muted" aria-live="polite">
          {results.length} clip{results.length === 1 ? '' : 's'}{searching ? ` for “${query.trim()}”` : ''}
        </p>
        <label className="flex items-center gap-1.5 text-[13px] text-ink-muted">
          Sort
          <select
            value={sort === 'match' && !searching ? 'popular' : sort}
            onChange={e => setSort(e.target.value as Sort)}
            className="h-9 rounded-xl bg-raised border border-line px-2 text-[13px] text-ink"
          >
            {searching && <option value="match">Best match</option>}
            <option value="popular">Most tried</option>
            <option value="az">A–Z</option>
          </select>
        </label>
      </div>

      {results.length === 0 ? (
        <div className="bg-surface border border-line rounded-[20px] p-6 text-center">
          <p className="text-[15px] font-semibold">No clips match</p>
          <p className="text-[13px] text-ink-muted mt-1">Try fewer words, or a category like “oral” or “creampie”.</p>
        </div>
      ) : (
        <ul className="space-y-2 md:space-y-0 md:grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 md:gap-3">
          {results.map(clip => {
            const tried = triedIds.has(clip.id);
            const wanted = wishlistIds.has(clip.id);
            const cats = getClipCategoryIds(clip).map(categoryName).filter(Boolean);
            return (
              <li key={clip.id}>
                <button
                  onClick={() => onOpen(clip)}
                  className="w-full h-full flex md:flex-col items-center md:items-stretch gap-3 p-2.5 pr-3 md:pr-2.5 rounded-[18px] bg-surface border border-line hover:border-line-strong text-left"
                >
                  <ClipThumb url={clip.link} className="w-[72px] h-[72px] md:w-full md:h-48" showPlay />
                  <span className="flex-1 min-w-0 md:px-1 md:pb-1">
                    <span className="block text-[15px] font-semibold leading-snug truncate">{clip.title}</span>
                    <span className="block text-[13px] text-ink-muted truncate">{cats.join(' · ')}</span>
                    <span className="mt-1 flex flex-wrap items-center gap-1.5">
                      {tried && <span className="h-6 px-2 rounded-full bg-action-tint text-action text-[11px] font-bold flex items-center gap-1"><Check className="w-3 h-3" /> Tried</span>}
                      {wanted && <span className="h-6 px-2 rounded-full bg-her-tint text-her text-[11px] font-bold flex items-center gap-1"><Heart className="w-3 h-3 fill-current" /> Want to try</span>}
                      {(clip.couplesTried || 0) > 0 && (
                        <span className="text-[11px] text-ink-muted flex items-center gap-1">
                          <Star className="w-3 h-3" /> {clip.couplesTried} couple{clip.couplesTried === 1 ? '' : 's'} tried
                        </span>
                      )}
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
